import { forwardRef } from "react";
import { Dialog, type DialogHandle } from "../app/components/ui/Dialog";
import { useThemeStore, type LanguageMode } from "../app/stores/themeStore";
import styles from "./ReferenceDialog.module.css";

interface Term {
  name: string;
  description: string;
}

interface ReferenceSection {
  title: string;
  terms?: Term[];
  code?: string | string[];
  note?: string;
}

const JS_SECTIONS: ReferenceSection[] = [
  {
    title: "Commands",
    terms: [
      { name: "move()", description: "Move Karel forward one corner" },
      {
        name: "turnLeft()",
        description: "Turn Karel 90° left (counter-clockwise)",
      },
      { name: "turnRight()", description: "Turn Karel 90° right (clockwise)" },
      { name: "turnAround()", description: "Turn Karel 180° around" },
      {
        name: "putBeeper()",
        description: "Put a beeper on the current corner",
      },
      { name: "pickBeeper()", description: "Pick a beeper up from the corner" },
      {
        name: "paintCorner(color)",
        description:
          "Paint the corner any HTML/CSS color, e.g. 'red' or '#ff0000'",
      },
      {
        name: "print(value)",
        description: "Print a value to the output console",
      },
      {
        name: "input()",
        description: "Read the next value from the input panel",
      },
      {
        name: "len(list)",
        description: "Number of items in a list (alias: length)",
      },
      { name: "int(value)", description: "Convert a value to a whole number" },
      { name: "str(value)", description: "Convert a value to text" },
    ],
  },
  {
    title: "Conditions",
    terms: [
      {
        name: 'canMove("forward")',
        description: "Can Karel move in a relative direction?",
      },
      {
        name: 'canMove("left") / canMove("right") / canMove("backward")',
        description: 'Relative directions, or "forward"',
      },
      {
        name: "frontIsClear() / frontIsBlocked()",
        description: "Is the way ahead open? (alias of canMove)",
      },
      {
        name: "leftIsClear() / leftIsBlocked()",
        description: "Is the way to the left open?",
      },
      {
        name: "rightIsClear() / rightIsBlocked()",
        description: "Is the way to the right open?",
      },
      {
        name: "beepersPresent() / noBeepersPresent()",
        description: "Is a beeper on this corner?",
      },
      {
        name: "karelAvenue() / karelStreet()",
        description: "Karel's current avenue and street (1-based)",
      },
      {
        name: "facingNorth() / notFacingNorth()",
        description: "Is Karel facing north?",
      },
      {
        name: "facingEast() / notFacingEast()",
        description: "Is Karel facing east?",
      },
      {
        name: "facingSouth() / notFacingSouth()",
        description: "Is Karel facing south?",
      },
      {
        name: "facingWest() / notFacingWest()",
        description: "Is Karel facing west?",
      },
      {
        name: "random(a, b) / random()",
        description:
          "Whole number from a to b inclusive, or a 50/50 coin flip with no arguments",
      },
      {
        name: "randomSeed(n)",
        description: "Seed the random generator for repeatable runs",
      },
    ],
  },
  {
    title: "Program structure",
    code: [
      `// single-line comment

/* multi-line comments may
   appear anywhere in code */`,
      `function main() {
  // your program starts here
  move();
}`,
      `function safeMove() {
  if (frontIsClear()) {
    move();
  }
}`,
    ],
    note: "main() runs automatically when you press Run.",
  },
  {
    title: "Conditional statements",
    code: [
      `if (frontIsBlocked()) {
  turnLeft();
}`,
      `if (beepersPresent()) {
  pickBeeper();
} else {
  putBeeper();
}`,
    ],
  },
  {
    title: "Iterative statements",
    code: [
      `for (let i = 0; i < 4; i++) {
  paintCorner('red');
  move();
}`,
      `while (frontIsClear()) {
  move();
}`,
      `repeat(4) {
  putBeeper();
  move();
}`,
    ],
  },
];

const PYTHON_SECTIONS: ReferenceSection[] = [
  {
    title: "Commands",
    terms: [
      { name: "move()", description: "Move Karel forward one corner" },
      {
        name: "turn_left()",
        description: "Turn Karel 90° left (counter-clockwise)",
      },
      { name: "turn_right()", description: "Turn Karel 90° right (clockwise)" },
      { name: "turn_around()", description: "Turn Karel 180° around" },
      {
        name: "put_beeper()",
        description: "Put a beeper on the current corner",
      },
      {
        name: "pick_beeper()",
        description: "Pick a beeper up from the corner",
      },
      {
        name: "paint_corner(color)",
        description:
          "Paint the corner any HTML/CSS color, e.g. 'red' or '#ff0000'",
      },
      {
        name: "print(value)",
        description: "Print a value to the output console",
      },
      {
        name: "input()",
        description: "Read the next value from the input panel",
      },
      { name: "len(list)", description: "Number of items in a list" },
      {
        name: "int(value) / str(value)",
        description: "Convert between numbers and text",
      },
    ],
  },
  {
    title: "Conditions",
    terms: [
      {
        name: 'can_move("forward")',
        description: "Can Karel move in a relative direction?",
      },
      {
        name: 'can_move("left") / can_move("right") / can_move("backward")',
        description: 'Relative directions, or "forward"',
      },
      {
        name: "front_is_clear() / front_is_blocked()",
        description: "Is the way ahead open? (alias of can_move)",
      },
      {
        name: "left_is_clear() / left_is_blocked()",
        description: "Is the way to the left open?",
      },
      {
        name: "right_is_clear() / right_is_blocked()",
        description: "Is the way to the right open?",
      },
      {
        name: "beepers_present() / no_beepers_present()",
        description: "Is a beeper on this corner?",
      },
      {
        name: "karel_avenue() / karel_street()",
        description: "Karel's current avenue and street (1-based)",
      },
      {
        name: "facing_north() / not_facing_north()",
        description: "Is Karel facing north?",
      },
      {
        name: "facing_east() / not_facing_east()",
        description: "Is Karel facing east?",
      },
      {
        name: "facing_south() / not_facing_south()",
        description: "Is Karel facing south?",
      },
      {
        name: "facing_west() / not_facing_west()",
        description: "Is Karel facing west?",
      },
      {
        name: "random(a, b) / random()",
        description:
          "Whole number from a to b inclusive, or a 50/50 coin flip with no arguments",
      },
      {
        name: "random_seed(n)",
        description: "Seed the random generator for repeatable runs",
      },
    ],
  },
  {
    title: "Program structure",
    code: [
      `# single-line comment
"""
multi-line comments may
appear anywhere in code
"""`,
      `def main():
    # your program starts here
    move()`,
      `def safe_move():
    if front_is_clear():
        move()`,
    ],
    note: "main() runs automatically when you press Run.",
  },
  {
    title: "Conditional statements",
    code: [
      `if front_is_blocked():
    turn_left()`,
      `if beepers_present():
    pick_beeper()
else:
    put_beeper()`,
    ],
  },
  {
    title: "Iterative statements",
    code: [
      `for i in range(4):
    paint_corner('red')
    move()`,
      `while front_is_clear():
    move()`,
    ],
  },
];

const SECTIONS: Record<LanguageMode, ReferenceSection[]> = {
  js: JS_SECTIONS,
  python: PYTHON_SECTIONS,
};

const LANGUAGE_LABELS: Record<LanguageMode, string> = {
  js: "JavaScript",
  python: "Python",
};

export const ReferenceDialog = forwardRef<DialogHandle>(
  function ReferenceDialog(_props, ref) {
    const languageMode = useThemeStore((s) => s.languageMode);
    const sections = SECTIONS[languageMode];

    return (
      <Dialog ref={ref} title="Reference" className={styles.reference}>
        <div className={styles.sections}>
          {sections.map((section) => (
            <section key={section.title} className={styles.section}>
              <h3 className={styles.sectionTitle}>{section.title}</h3>

              {section.terms && (
                <dl className={styles.terms}>
                  {section.terms.map((term) => (
                    <div key={term.name} className={styles.term}>
                      <dt className={styles.termName}>{term.name}</dt>
                      <dd className={styles.termDescription}>
                        {term.description}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}

              {section.code && (
                <div className={styles.codeBlocks}>
                  {(Array.isArray(section.code)
                    ? section.code
                    : [section.code]
                  ).map((block, i) => (
                    <pre key={i} className={styles.code}>
                      <code>{block}</code>
                    </pre>
                  ))}
                </div>
              )}

              {section.note && <p className={styles.note}>{section.note}</p>}
            </section>
          ))}
        </div>

        <p className={styles.languageHint}>
          Showing <strong>{LANGUAGE_LABELS[languageMode]}</strong> examples.
          Switch the editor language in Settings.
        </p>
      </Dialog>
    );
  },
);
